import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { ComponentCalculationField, ComponentGet, ComponentService } from '@app/services/component.service';
import { ProductUnitItem } from '@app/services/product.service';
import { catchError, debounceTime, EMPTY, from, groupBy, map, mergeMap, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-component-calculation',
    templateUrl: './component-calculation.component.html',
    styleUrls: ['./component-calculation.component.css'],
    standalone: false,
})
export class ComponentCalculationComponent implements OnDestroy {
    @Input()
    public get?: ComponentGet;

    @Output()
    public getChange = new EventEmitter<ComponentGet>();

    @Input()
    public productUnits: ProductUnitItem[] = [];

    private readonly calculate$ = new Subject<ComponentCalculationChange>();

    private readonly destroy$ = new Subject<void>();

    constructor(
        private component: ComponentService,
        private modal: ModalService,
    ) {
        this.calculate$
            .pipe(
                groupBy(({ changedcol }) => changedcol),
                mergeMap((group) =>
                    group.pipe(
                        debounceTime(500),
                        switchMap((change) =>
                            from(this.component.calculateComponent({ ...change.get, changedcol: change.changedcol })).pipe(
                                map((value) => ({ value, change })),
                                catchError((error) => {
                                    console.error('calculateComponent failed:', error);
                                    return EMPTY;
                                }),
                            ),
                        ),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: ({ value, change }) => {
                    if (!value || this.get !== change.get) return;

                    if ('' + value.errorcode !== '0') {
                        this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: value.errormessage });
                        return;
                    }

                    change.get.quantity = value.quantity;

                    if (change.changedcol !== 'input_quantity') {
                        change.get.input_quantity = value.input_quantity;
                    }

                    this.getChange.emit(change.get);
                },
            });
    }

    public calculate(changedcol: ComponentCalculationField) {
        if (!this.get) return;

        this.getChange.emit(this.get);
        this.calculate$.next({ get: this.get, changedcol });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.calculate$.complete();
    }
}

interface ComponentCalculationChange {
    get: ComponentGet;
    changedcol: ComponentCalculationField;
}
